import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import { Amplify } from 'aws-amplify';

import { initializeApp } from 'firebase/app';
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import { getAnalytics, setUserProperties } from 'firebase/analytics';

import * as amplitudeBrowser from '@amplitude/analytics-browser';
import { RemoteConfig_SupportNumber } from '@brightside-web/desktop/data-access/shared';
import { PerformanceTrackingConfigure } from '@micro-core/lib-config';

import airbridge from 'airbridge-web-sdk-loader';

const getSubDomain = () => {
  const hostnameSplit = window.location.hostname.toLowerCase().split('.');
  const tldDomain = hostnameSplit[hostnameSplit.length - 2];
  if (hostnameSplit[0] === 'app' || tldDomain !== 'gobrightside') {
    return '';
  } else {
    return hostnameSplit[0];
  }
};

const getEnvironment = () => {
  const hostname = window.location.hostname.toLowerCase();

  if (environment.production) {
    return 'prod';
  } else if (hostname.includes('stage')) {
    return 'stage';
  } else {
    return 'dev2';
  }
};

const firebaseApp = initializeApp(environment.firebase);
const remoteConfig = getRemoteConfig(firebaseApp);
const fbAnalytics = getAnalytics(firebaseApp);
new PerformanceTrackingConfigure().setUpConfig(environment);


amplitudeBrowser.init(environment.amplitude.apiKey, {
  autocapture: {
    attribution: true,
    pageViews: false,
    sessions: true,
    formInteractions: false,
    fileDownloads: false,
    elementInteractions: false,
  },
  appVersion: environment.appVersion,
});

// amplitude.getInstance().init(environment.amplitude.apiKey, undefined, {
//   includeUtm: true,
//   includeGclid: true,
//   includeFbclid: true,
//   includeReferrer: true,
// });
//
// amplitude.getInstance().setVersionName(environment.appVersion);

// default configs for RemoteConfig.
remoteConfig.defaultConfig = {
  supportNumber: '8559401507',
  bankHolidays:
    '2021-09-06,2021-10-11,2021-11-11,2021-11-25,2022-01-17,2022-02-21,2022-05-30,2022-06-20,2022-07-04,2022-09-05,2022-10-10,2022-11-11,2022-11-24,2022-12-26,2023-01-02,2023-01-16,2023-02-20,2023-05-29,2023-06-19,2023-07-04,2023-09-04,2023-10-09,2023-11-23,2023-12-25,2024-01-01,2024-01-15,2024-02-19,2024-05-27,2024-06-19,2024-07-04,2024-09-02,2024-10-14,2024-11-11,2024-11-28,2024-12-25,2025-01-01,2025-01-20,2025-02-17,2025-05-26,2025-06-19,2025-07-04,2025-09-01,2025-10-13,2025-11-11,2025-11-27,2025-12-25',
  unlockedCaseTypes: 'Emergency_Savings,Emergency_Cash,Credit_Score',
};

const company = getSubDomain();
const properties: any = {};
if (company) {
  properties['company'] = company;
}
const env = getEnvironment();
if (env) {
  properties['environment'] = env;
}

setUserProperties(fbAnalytics, properties);

remoteConfig.settings.minimumFetchIntervalMillis = environment.production ? 3000 : 0;

fetchAndActivate(remoteConfig)
  .then(() => {
    console.log(`Support number: ${getValue(remoteConfig, RemoteConfig_SupportNumber).asString()}`);
  })
  .catch((err) => {
    if (!environment.production) {
      console.log('debug: a firebase error has occurred', err);
    }
  });

const getTimeZone = () => {
  const formatParts = Intl.DateTimeFormat('en', { timeZoneName: 'short' }).format().split(' ');

  return formatParts[formatParts.length - 1];
};

const getBrowser = () => {
  const userAgent = navigator.userAgent;
  const browserMapping: { [key: string]: { validMatch: string[]; extraChecks?: Function[] } } = {
    Opera: {
      validMatch: ['Opera', 'OPR'],
    },
    Chrome: {
      validMatch: ['Chrome'],
    },
    Safari: {
      validMatch: ['Safari'],
    },
    Firefox: {
      validMatch: ['Firefox'],
    },
    IE: {
      validMatch: ['MSIE'],
      extraChecks: [() => Object.keys(document).includes('documentMode')],
    },
  };

  return (
    Object.keys(browserMapping).filter((key: string) => {
      const checkDetails = browserMapping[key];

      if (checkDetails.validMatch.some((browserName) => userAgent.includes(browserName))) {
        //If there are extra checks, make sure they all return true
        if (checkDetails.extraChecks && !checkDetails.extraChecks.every((checkFunc) => checkFunc())) {
          return false;
        }

        return true;
      }

      return false;
    })[0] || 'Unknown'
  );
};

// does anybody know why we are not simply using a UUID library to generate a UUID???
const generateUUID = () => {
  let d = new Date().getTime(); //Timestamp
  let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-bitwise
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-bitwise
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};
// tslint:enable:no-bitwise

const getDeviceId = () => {
  //don't want to use SessionStorage here.
  let deviceId = window.localStorage['DEVICE_ID'];
  if (!deviceId) {
    deviceId = generateUUID();
    window.localStorage['DEVICE_ID'] = deviceId;
  }
  return deviceId;
};

const getStandardHeaders = () => {
  const headers: { [key: string]: any } = {
    'Content-Type': 'application/json',
    'X-BS-OS-Name': 'Desktop',
    'X-BS-Model': getBrowser(),
    'X-BS-Timezone': getTimeZone(),
    'X-BS-GmtOffset': `${new Date().getTimezoneOffset() / -60}`,
    'X-BS-DeviceId': getDeviceId(),
  };

  return headers;
};

//sso domain for hosted ui
const domain = environment.hostedDomain;

//sso callback
let location = window.location.origin;
if (!location.endsWith('/')) {
  location = `${location}/`;
}

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic()
  .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
  .then((module) => {
    //The code is needed to support the native application sending bridge events
    //into the message bus for the micro application
    window.app = module;
  })
  .catch((err) => console.error(err));

// allow both formats.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line no-bitwise

Amplify.configure({
  API: {
    REST: {
      'api-mobile': {
        endpoint: environment.awsmobile.endpoints['api-mobile'].endpoint,
        region: 'us-west-2',
      },
      'api-mobile-noauth': {
        endpoint: environment.awsmobile.endpoints['api-mobile-noauth'].endpoint,
        region: 'us-west-2',
      },
      'cdn-mobile-noauth': {
        endpoint: environment.awsmobile.endpoints['cdn-mobile-noauth'].endpoint,
        region: 'us-west-2',
      },
    },
  },
});


airbridge.init({
  app: environment.airbridge.app,
  webToken: environment.airbridge.webToken,
  autoStartTrackingEnabled: false
});

async function waitForAmplitudeDeviceId(
  maxAttempts = 10,
  intervalMs = 500
): Promise<string | undefined> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const deviceId = amplitudeBrowser.getDeviceId();
    if (deviceId) {
      return deviceId;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return undefined;
}

(async () => {
  const deviceId = await waitForAmplitudeDeviceId();

  if (deviceId) {
    airbridge.setDeviceAlias('amplitude_device_id', deviceId);
  }

  airbridge.startTracking();
})();
